import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronRight, Loader2, Send, AlertCircle, ArrowLeft } from 'lucide-react';
import { usePrices, formatPrice, formatEUR, getBundles } from '../hooks/usePrices';
import { useLang } from '../LangContext';
import { submitLead } from '../lib/api';

import { pickI18N } from './configurator/translations';
import {
  TYPES, COLORS, MESHES,
  NEEDS_OPENING_DIR, NEEDS_SLIDE_DIR, NEEDS_CROSSBAR,
  productNameFor, shortNameFor,
  computePrice, basePriceFromCatalog,
} from './configurator/constants';
import { encodeCart, decodeCart, newUid } from './configurator/cartCodec';
import {
  StepIndicator, TypeCard, ColorChip, MeshOption, DirectionPicker,
} from './configurator/atoms';
import SuccessScreen from './configurator/SuccessScreen';
import SummaryAside from './configurator/SummaryAside';
import BundleBanner from './configurator/BundleBanner';
import ShareCard from './configurator/ShareCard';

/**
 * Configurator — orchestrator.
 *
 * Owns the state (step, cart, draft, errors, submit status, bundle plan)
 * and wires it into the presentational sub-components in `./configurator/`.
 * All math + i18n + base64 helpers live in their own files so this orches-
 * trator can stay readable.
 */
export default function Configurator() {
  const { lang } = useLang();
  const prices = usePrices();
  const t = pickI18N(lang);

  // ---- Core state
  const [step, setStep] = useState(1);
  const [selectedTypeIdx, setSelectedTypeIdx] = useState(null);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [colorIdx, setColorIdx] = useState(null);
  const [meshIdx, setMeshIdx] = useState(null);
  const [openingDir, setOpeningDir] = useState(null);
  const [slideDir, setSlideDir] = useState(null);
  const [crossbarMm, setCrossbarMm] = useState('');
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [copied, setCopied] = useState(false);

  // ---- URL-driven state
  const [bundlePlan, setBundlePlan] = useState([]);
  const [bundleInfo, setBundleInfo] = useState(null);
  const [restoredFromUrl, setRestoredFromUrl] = useState(false);
  const [referrerCfg, setReferrerCfg] = useState(null);

  // ---- Edit + auto-add flags
  const [editingIdx, setEditingIdx] = useState(null);
  const [pendingAutoAdd, setPendingAutoAdd] = useState(false);

  // ---- Hydrate cart from ?cfg=<base64> on first mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('cfg');
    if (!token) return;
    const decoded = decodeCart(token);
    if (!decoded || !decoded.length) return;
    setReferrerCfg(token);
    const restored = decoded.map((d) => ({
      uid: newUid(),
      category: d.c,
      slug: d.s,
      width: Number(d.w) || 1000,
      height: Number(d.h) || 1200,
      colorId: d.co || 'white',
      meshId: d.me || 'standard',
      estPrice: null,
    }));
    setCart(restored);
    setStep(4);
    setRestoredFromUrl(true);
    setTimeout(() => setRestoredFromUrl(false), 5000);
  }, []);

  // ---- Expand ?bundle=<id> into a flat product queue.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!prices || !prices.bundles) return;
    const params = new URLSearchParams(window.location.search);
    const bundleId = params.get('bundle');
    if (!bundleId) return;
    const rawBundle = (prices.bundles.items || []).find((b) => b.id === bundleId);
    if (!rawBundle) return;
    const plan = (rawBundle.products || []).flatMap((p) =>
      Array.from({ length: p.qty || 1 }, () => ({ category: p.category, slug: p.slug }))
    );
    if (!plan.length) return;
    if (bundleInfo && bundleInfo.id === bundleId) return;
    const name = (rawBundle.name && (rawBundle.name[lang] || rawBundle.name.nl)) || bundleId;
    setBundleInfo({
      id: bundleId,
      name,
      total: plan.length,
      original: rawBundle.products || [],
    });
    const first = plan[0];
    const idx = TYPES.findIndex((tt) => tt.category === first.category && tt.slug === first.slug);
    if (idx >= 0) setSelectedTypeIdx(idx);
    setBundlePlan(plan.slice(1));
    setStep(2);
  }, [prices, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Derived state from current draft
  const activeType = selectedTypeIdx != null ? TYPES[selectedTypeIdx] : null;
  const priceInfo = useMemo(
    () => (activeType ? formatPrice(prices, activeType.category, activeType.slug, lang) : null),
    [prices, activeType, lang]
  );
  const basePriceNum = useMemo(
    () => (activeType ? basePriceFromCatalog(prices, activeType.category, activeType.slug) : null),
    [prices, activeType]
  );
  const productName = useMemo(
    () => (activeType ? productNameFor(activeType.slug, lang) : ''),
    [activeType, lang]
  );
  const color = colorIdx != null ? COLORS[colorIdx] : null;
  const mesh = meshIdx != null ? MESHES[meshIdx] : null;
  const needsOpeningDir = !!activeType && NEEDS_OPENING_DIR.has(activeType.slug);
  const needsSlideDir = !!activeType && NEEDS_SLIDE_DIR.has(activeType.slug);
  const needsCrossbar = !!activeType && NEEDS_CROSSBAR.has(activeType.slug);

  const widthNum = width === '' ? NaN : Number(width);
  const heightNum = height === '' ? NaN : Number(height);
  const sizeValid = Number.isFinite(widthNum) && Number.isFinite(heightNum)
    && widthNum >= 300 && widthNum <= 3000
    && heightNum >= 300 && heightNum <= 3000;

  const estPrice = useMemo(
    () => (basePriceNum && color && mesh && sizeValid
      ? computePrice({ basePrice: basePriceNum, width: widthNum, height: heightNum, colorUp: color.up, meshUp: mesh.up })
      : null),
    [basePriceNum, widthNum, heightNum, color, mesh, sizeValid]
  );

  // ---- Draft → cart helpers
  const buildDraftItem = () => ({
    uid: newUid(),
    category: activeType.category,
    slug: activeType.slug,
    productName,
    width: widthNum,
    height: heightNum,
    colorId: color.id,
    colorLabel: t.colorNames[color.id],
    colorUp: color.up,
    meshId: mesh.id,
    meshLabel: t.meshNames[mesh.id],
    meshUp: mesh.up,
    openingDir: needsOpeningDir ? openingDir : null,
    slideDir: needsSlideDir ? slideDir : null,
    crossbarMm: needsCrossbar && crossbarMm !== '' ? Math.round(Number(crossbarMm)) : null,
    estPrice,
  });

  const resetDraft = () => {
    setSelectedTypeIdx(null);
    setWidth(''); setHeight('');
    setColorIdx(null); setMeshIdx(null);
    setOpeningDir(null); setSlideDir(null);
    setCrossbarMm('');
    setEditingIdx(null);
  };

  const addItem = (then = 'list') => {
    const errs = {};
    if (!activeType) errs.type = t.errType;
    if (!sizeValid) errs.size = t.errSize;
    if (!color || !mesh) errs.options = t.errOptions;
    if (needsOpeningDir && !openingDir) errs.options = t.errOptions;
    if (needsSlideDir && !slideDir) errs.options = t.errOptions;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const item = buildDraftItem();
    if (editingIdx != null) {
      setCart((c) => c.map((it, i) => (i === editingIdx ? item : it)));
      setEditingIdx(null);
      resetDraft(); setErrors({}); setStep(4);
      return;
    }
    setCart((c) => [...c, item]);
    setErrors({});
    if (bundlePlan.length > 0) {
      const next = bundlePlan[0];
      const idx = TYPES.findIndex((tt) => tt.category === next.category && tt.slug === next.slug);
      setBundlePlan(bundlePlan.slice(1));
      setSelectedTypeIdx(idx >= 0 ? idx : null);
      setWidth(''); setHeight(''); setColorIdx(null); setMeshIdx(null);
      setOpeningDir(null); setSlideDir(null); setCrossbarMm('');
      setStep(2);
      return;
    }
    resetDraft();
    setStep(then === 'continue' ? 4 : 1);
  };

  const startEdit = (i) => {
    const it = cart[i];
    if (!it) return;
    const tIdx = TYPES.findIndex((tt) => tt.category === it.category && tt.slug === it.slug);
    setEditingIdx(i);
    setSelectedTypeIdx(tIdx >= 0 ? tIdx : null);
    setWidth(String(it.width || ''));
    setHeight(String(it.height || ''));
    setColorIdx(COLORS.findIndex((c) => c.id === it.colorId));
    setMeshIdx(MESHES.findIndex((m) => m.id === it.meshId));
    setOpeningDir(it.openingDir || null);
    setSlideDir(it.slideDir || null);
    setCrossbarMm(it.crossbarMm != null ? String(it.crossbarMm) : '');
    setErrors({});
    setStep(2);
  };

  const removeItem = (idx) => setCart((c) => c.filter((_, i) => i !== idx));
  const grandTotal = useMemo(() => cart.reduce((sum, it) => sum + (it.estPrice || 0), 0), [cart]);

  // ---- Bundle progress
  const typeProgress = useMemo(() => {
    if (!bundleInfo || !activeType) return null;
    const target = (bundleInfo.original || []).find(
      (p) => p.category === activeType.category && p.slug === activeType.slug
    );
    if (!target) return null;
    const doneSame = cart.filter(
      (it) => it.category === activeType.category && it.slug === activeType.slug
    ).length;
    return { current: doneSame + 1, total: target.qty || 1 };
  }, [bundleInfo, activeType, cart]);
  const bundleRemaining = bundleInfo ? Math.max(0, bundleInfo.total - cart.length) : 0;

  // ---- Auto-add on double-click (effect runs after React commits state).
  useEffect(() => {
    if (!pendingAutoAdd) return;
    const needsDir = needsOpeningDir || needsSlideDir;
    const dirOk = (!needsOpeningDir || openingDir) && (!needsSlideDir || slideDir);
    if (color && mesh && activeType && sizeValid && (!needsDir || dirOk)) {
      setPendingAutoAdd(false);
      addItem(bundleInfo ? 'list' : 'continue');
    } else {
      setPendingAutoAdd(false);
    }
  }, [pendingAutoAdd, color, mesh, activeType, sizeValid, openingDir, slideDir, needsOpeningDir, needsSlideDir]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Re-price cart items when prices book or language changes.
  useEffect(() => {
    if (!cart.length) return;
    if (!prices || !prices.prices) return;
    const needsHydration = cart.some((it) => it.estPrice == null || !it.productName);
    if (!needsHydration) return;
    const hydrated = cart.map((it) => {
      const basePrice = basePriceFromCatalog(prices, it.category, it.slug);
      const col = COLORS.find((c) => c.id === it.colorId) || COLORS[0];
      const me = MESHES.find((m) => m.id === it.meshId) || MESHES[0];
      return {
        ...it,
        productName: productNameFor(it.slug, lang),
        colorLabel: t.colorNames[col.id],
        colorUp: col.up,
        meshLabel: t.meshNames[me.id],
        meshUp: me.up,
        estPrice: computePrice({ basePrice, width: it.width, height: it.height, colorUp: col.up, meshUp: me.up }),
      };
    });
    setCart(hydrated);
  }, [prices, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Step nav
  const goNext = () => {
    const errs = {};
    if (step === 1 && !activeType) errs.type = t.errType;
    if (step === 2 && !sizeValid) errs.size = t.errSize;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStep((s) => Math.min(4, s + 1));
  };
  const goBack = () => { setErrors({}); setStep((s) => Math.max(1, s - 1)); };

  // ---- Submit
  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = t.errName;
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = t.errMail;
    if (cart.length === 0) errs.size = t.errSize;
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const itemLines = cart.flatMap((it, i) => {
      const lines = [
        `--- ${t.itemNumFmt.replace('{n}', String(i + 1))} ---`,
        `${t.steps[0]}: ${it.productName} (${it.category}/${it.slug})`,
        `${t.steps[1]}: ${it.width} × ${it.height} ${t.cm}`,
        `${t.color}: ${it.colorLabel} (+${formatEUR(it.colorUp) || '0,-- €'})`,
        `${t.mesh}: ${it.meshLabel} (+${formatEUR(it.meshUp) || '0,-- €'})`,
      ];
      if (it.openingDir) lines.push(`${t.openingDir}: ${it.openingDir === 'L' ? t.dirLeft : t.dirRight}`);
      if (it.slideDir) lines.push(`${t.slideDir}: ${it.slideDir === 'L' ? t.dirLeft : t.dirRight}`);
      if (it.crossbarMm) lines.push(`${t.crossbar}: ${it.crossbarMm} mm`);
      lines.push(`${t.estPrice}: ${formatEUR(it.estPrice) || '?'}`);
      lines.push('');
      return lines;
    });
    const msg = [
      ...itemLines,
      `${t.totalLabel}: ${formatEUR(grandTotal) || '0,-- €'}`,
      form.notes ? `\n${t.notes}: ${form.notes}` : '',
    ].filter(Boolean).join('\n');
    const primaryCategory = cart[0]?.category || 'other';

    setSubmitting(true); setErrors({});
    try {
      const data = await submitLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        product: primaryCategory,
        message: msg,
        language: lang,
        source: 'configurator',
        referrer_cfg: referrerCfg,
      });
      setSubmittedId(data.id || 'ok');
    } catch (err) {
      setErrors({ net: t.errNet });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(1); resetDraft();
    setCart([]); setForm({ name: '', email: '', phone: '', notes: '' });
    setErrors({}); setSubmittedId(null);
  };

  // ---- Cross-sell + disclaimer + share helpers
  const crossSellBundle = useMemo(() => {
    const data = getBundles(prices, lang);
    if (!data || !data.items?.length) return null;
    const items = data.items.filter((b) => !b.soldOut && b.save > 0);
    if (!items.length) return null;
    const userSlugs = new Set(cart.map((it) => `${it.category}/${it.slug}`));
    const matched = items.find((b) => b.products.some((p) => userSlugs.has(`${p.category}/${p.slug}`)));
    const popular = items.find((b) => b.highlight);
    const biggest = [...items].sort((a, b) => b.save - a.save)[0];
    const pick = matched || popular || biggest;
    return pick ? { ...pick, saveLabelText: data.saveLabel } : null;
  }, [prices, lang, cart]);

  const disclaimerLabels = useMemo(() => {
    const l = prices?.bundles?.labels || {};
    const pick = (obj) => obj && (obj[lang] || obj.nl);
    return {
      whiteStandardNote: pick(l.whiteStandard) || '',
      vatNote: pick(l.vatNote) || '',
      shippingNote: pick(l.shippingNote) || '',
    };
  }, [prices, lang]);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined' || !cart.length) return '';
    const token = encodeCart(cart);
    if (!token) return '';
    const u = new URL(window.location.href);
    u.searchParams.set('cfg', token);
    return u.toString();
  }, [cart]);

  const shareMessage = `${t.shareMsgFmt
    .replace('{n}', String(cart.length))
    .replace('{total}', formatEUR(grandTotal) || '—')}\n${shareUrl}`;

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (clipboardErr) {
      const ta = document.createElement('textarea');
      ta.value = shareUrl; document.body.appendChild(ta); ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      } catch (execErr) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[Configurator] copy failed:', clipboardErr?.message, execErr?.message);
        }
      }
      document.body.removeChild(ta);
    }
  };

  // ============================================================
  // Render
  // ============================================================

  if (submittedId) {
    return (
      <SuccessScreen
        t={t}
        lang={lang}
        onReset={reset}
        crossSellBundle={crossSellBundle}
        disclaimerLabels={disclaimerLabels}
      />
    );
  }

  const proceedDisabled = !color || !mesh
    || (needsOpeningDir && !openingDir)
    || (needsSlideDir && !slideDir);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-testid="configurator-app">
      <div className="lg:col-span-8">
        {bundleInfo && step < 4 && (
          <BundleBanner
            t={t}
            bundleInfo={bundleInfo}
            cart={cart}
            activeType={activeType}
            productName={productName}
            typeProgress={typeProgress}
            remaining={bundleRemaining}
          />
        )}

        <StepIndicator step={step} labels={t.steps} />

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step-1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
              <h2 className="font-display text-[24px] sm:text-[28px] text-[#1F1D1A] leading-tight">{t.typeTitle}</h2>
              <p className="text-[14.5px] text-[#5C5852] mt-2 mb-6">{t.typeSub}</p>
              {errors.type && (
                <p data-testid="cfg-err-type" className="mb-4 inline-flex items-center gap-2 text-[13px] text-[#C03A1A]"><AlertCircle className="w-4 h-4" /> {errors.type}</p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {TYPES.map((entry, i) => (
                  <TypeCard
                    key={`${entry.category}-${entry.slug}`}
                    entry={entry}
                    selected={i === selectedTypeIdx}
                    onClick={() => setSelectedTypeIdx(i)}
                    onDoubleClick={() => { setSelectedTypeIdx(i); setStep(2); }}
                    productName={shortNameFor(entry.slug, lang)}
                    priceDisplay={formatPrice(prices, entry.category, entry.slug, lang)}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step-2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
              <h2 className="font-display text-[24px] sm:text-[28px] text-[#1F1D1A] leading-tight">{t.sizeTitle}</h2>
              <p className="text-[14.5px] text-[#5C5852] mt-2 mb-6">{t.sizeSub}</p>
              <div className="grid grid-cols-2 gap-4 max-w-md">
                {[
                  { id: 'w', label: t.width, value: width, set: setWidth },
                  { id: 'h', label: t.height, value: height, set: setHeight },
                ].map((f) => (
                  <label key={f.id} className="block">
                    <span className="block text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-2">{f.label}</span>
                    <div className="relative">
                      <input
                        type="number" inputMode="numeric" min={300} max={3000} step={1}
                        value={f.value} placeholder="—"
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '') f.set('');
                          else f.set(String(Math.round(Number(v))));
                        }}
                        onFocus={(e) => e.target.select()}
                        data-testid={`cfg-size-${f.id}`}
                        className="w-full pl-4 pr-12 py-3 rounded-xl border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[18px] font-semibold tabular-nums placeholder:text-[#E5E0D2]"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] text-[#5C5852]">{t.cm}</span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.size && (
                <p data-testid="cfg-err-size" className="mt-3 inline-flex items-center gap-2 text-[13px] text-[#C03A1A]"><AlertCircle className="w-4 h-4" /> {errors.size}</p>
              )}
              {sizeValid && (
                <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FAF8F2] border border-[#E5E0D2] text-[12.5px] text-[#5C5852] tabular-nums">
                  {widthNum} × {heightNum} {t.cm} · {(widthNum * heightNum / 1000000).toFixed(2)} m²
                </div>
              )}
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step-3" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
              <h2 className="font-display text-[24px] sm:text-[28px] text-[#1F1D1A] leading-tight">{t.optionsTitle}</h2>
              <p className="text-[14.5px] text-[#5C5852] mt-2 mb-6">{t.optionsSub}</p>

              <p className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-3">{t.color}</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {COLORS.map((c, i) => (
                  <ColorChip
                    key={c.id}
                    color={c}
                    selected={i === colorIdx}
                    onClick={() => setColorIdx(i)}
                    onDoubleClick={() => {
                      setColorIdx(i);
                      if (meshIdx != null) setPendingAutoAdd(true);
                    }}
                    label={t.colorNames[c.id]}
                  />
                ))}
              </div>

              <p className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-3">{t.mesh}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {MESHES.map((m, i) => (
                  <MeshOption
                    key={m.id}
                    mesh={m}
                    selected={i === meshIdx}
                    onClick={() => setMeshIdx(i)}
                    onDoubleClick={() => {
                      setMeshIdx(i);
                      if (colorIdx != null) setPendingAutoAdd(true);
                    }}
                    label={t.meshNames[m.id]}
                    desc={t.meshDescs[m.id]}
                  />
                ))}
              </div>

              {(needsOpeningDir || needsSlideDir) && (
                <>
                  <p className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mt-8 mb-3">
                    {needsOpeningDir ? t.openingDir : t.slideDir}
                  </p>
                  <DirectionPicker
                    value={needsOpeningDir ? openingDir : slideDir}
                    onChange={needsOpeningDir ? setOpeningDir : setSlideDir}
                    labels={{ left: t.dirLeft, right: t.dirRight }}
                  />
                </>
              )}

              {needsCrossbar && (
                <>
                  <p className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mt-8 mb-1">{t.crossbar}</p>
                  <p className="text-[12px] text-[#5C5852]/80 mb-3">{t.crossbarHint}</p>
                  <label className="block max-w-xs">
                    <div className="relative">
                      <input
                        type="number" inputMode="numeric" min={300} max={3000} step={1}
                        value={crossbarMm} placeholder={t.crossbarPlaceholder}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCrossbarMm(v === '' ? '' : String(Math.round(Number(v))));
                        }}
                        onFocus={(e) => e.target.select()}
                        data-testid="cfg-crossbar"
                        className="w-full pl-4 pr-12 py-3 rounded-xl border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[16px] font-semibold tabular-nums placeholder:text-[#E5E0D2]"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] text-[#5C5852]">mm</span>
                    </div>
                  </label>
                </>
              )}
            </motion.div>
          )}

          {step === 4 && (
            <motion.form key="step-4" onSubmit={onSubmit} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }}>
              {restoredFromUrl && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  data-testid="cfg-restored-banner"
                  className="mb-6 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#7FC242]/15 border border-[#7FC242]/40 text-[#3D5F1F] text-[12.5px] font-semibold"
                >
                  <Check className="w-4 h-4" /> {t.shareRestored}
                </motion.div>
              )}

              {bundleInfo && cart.length >= bundleInfo.total && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="cfg-bundle-complete"
                  className="mb-7 rounded-2xl bg-gradient-to-br from-[#7FC242]/15 to-[#F26B1A]/10 border border-[#7FC242]/40 p-5"
                >
                  <div className="flex items-start gap-3">
                    <span className="inline-flex w-10 h-10 rounded-full bg-[#7FC242] text-white items-center justify-center shrink-0">
                      <Check className="w-5 h-5" strokeWidth={3} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-bold text-[#1F1D1A]">{t.bundleCompleteTitle}</p>
                      <p className="text-[13px] text-[#5C5852] mt-1">{t.bundleCompleteSub}</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {cart.length > 0 && (
                <div className="mb-7" data-testid="cfg-cart">
                  <p className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-3">{t.yourItems}</p>
                  <ul className="space-y-2">
                    {cart.map((it, i) => (
                      <li
                        key={it.uid || `cart-${i}`}
                        data-testid={`cfg-cart-item-${i}`}
                        className="flex items-start justify-between gap-3 p-3 rounded-xl bg-white border border-[#E5E0D2]"
                      >
                        <div className="min-w-0">
                          <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#F26B1A]">
                            {t.itemNumFmt.replace('{n}', String(i + 1))}
                          </p>
                          <p className="text-[14px] font-semibold text-[#1F1D1A] mt-0.5">{it.productName}</p>
                          <p className="text-[12px] text-[#5C5852] mt-0.5 tabular-nums">
                            {it.width} × {it.height} {t.cm} · {it.colorLabel} · {it.meshLabel}
                            {it.openingDir ? ` · ${t.openingDir}: ${it.openingDir === 'L' ? t.dirLeft : t.dirRight}` : ''}
                            {it.slideDir ? ` · ${t.slideDir}: ${it.slideDir === 'L' ? t.dirLeft : t.dirRight}` : ''}
                            {it.crossbarMm ? ` · ${t.crossbar}: ${it.crossbarMm} mm` : ''}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className="font-display text-[18px] tabular-nums text-[#1F1D1A]">{formatEUR(it.estPrice)}</span>
                          <div className="flex items-center gap-2 text-[11px]">
                            <button
                              type="button"
                              onClick={() => startEdit(i)}
                              data-testid={`cfg-cart-edit-${i}`}
                              className="text-[#F26B1A] hover:text-[#D85A0A] font-semibold underline underline-offset-2"
                            >
                              {t.edit}
                            </button>
                            <span className="text-[#E5E0D2]">·</span>
                            <button
                              type="button"
                              onClick={() => removeItem(i)}
                              data-testid={`cfg-cart-remove-${i}`}
                              className="text-[#5C5852] hover:text-[#C03A1A] underline underline-offset-2"
                            >
                              {t.remove}
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {!bundleInfo && (
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      data-testid="cfg-add-another-step4"
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-dashed border-[#F26B1A]/50 text-[#F26B1A] text-[12.5px] font-semibold hover:bg-[#F26B1A]/5 transition-colors"
                    >
                      + {t.addAnother}
                    </button>
                  )}
                </div>
              )}

              {cart.length > 0 && (
                <ShareCard
                  t={t}
                  copied={copied}
                  shareUrl={shareUrl}
                  onCopy={copyShareLink}
                  shareMessage={shareMessage}
                />
              )}

              <h2 className="font-display text-[24px] sm:text-[28px] text-[#1F1D1A] leading-tight">{t.contactTitle}</h2>
              <p className="text-[14.5px] text-[#5C5852] mt-2 mb-6">{t.contactSub}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <label className="block sm:col-span-2">
                  <span className="block text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-2">{t.name}</span>
                  <input
                    type="text" data-testid="cfg-name"
                    value={form.name} placeholder={t.namePh}
                    autoFocus={!!bundleInfo && cart.length >= (bundleInfo?.total || 0)}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[15px]"
                  />
                  {errors.name && <p data-testid="cfg-err-name" className="mt-1.5 text-[12.5px] text-[#C03A1A]">{errors.name}</p>}
                </label>
                <label className="block">
                  <span className="block text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-2">{t.email}</span>
                  <input
                    type="email" data-testid="cfg-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[15px]"
                  />
                  {errors.email && <p data-testid="cfg-err-email" className="mt-1.5 text-[12.5px] text-[#C03A1A]">{errors.email}</p>}
                </label>
                <label className="block">
                  <span className="block text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-2">{t.phone}</span>
                  <input
                    type="tel" data-testid="cfg-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[15px]"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="block text-[12.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-2">{t.notes}</span>
                  <textarea
                    data-testid="cfg-notes" value={form.notes} rows={3} placeholder={t.notesPh}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[15px] resize-none"
                  />
                </label>
              </div>
              {errors.net && (
                <p data-testid="cfg-err-net" className="mt-3 inline-flex items-center gap-2 text-[13px] text-[#C03A1A]"><AlertCircle className="w-4 h-4" /> {errors.net}</p>
              )}
            </motion.form>
          )}
        </AnimatePresence>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {step > 1 && (
            <button
              type="button" onClick={goBack} data-testid="cfg-back"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-[#E5E0D2] text-[13px] font-semibold text-[#1F1D1A] hover:border-[#F26B1A] hover:text-[#F26B1A] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> {t.back}
            </button>
          )}
          {step < 3 && (
            <button
              type="button" onClick={goNext}
              disabled={step === 1 && !activeType}
              data-testid="cfg-next"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t.next} <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {step === 3 && (
            <>
              {!bundleInfo && (
                <button
                  type="button" onClick={() => addItem('list')}
                  disabled={proceedDisabled}
                  data-testid="cfg-add-another"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white border-2 border-[#F26B1A] text-[#F26B1A] text-[13px] font-semibold hover:bg-[#F26B1A]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  + {t.addAnother}
                </button>
              )}
              <button
                type="button" onClick={() => addItem('continue')}
                disabled={proceedDisabled}
                data-testid="cfg-proceed"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {bundleInfo && bundlePlan.length > 0 ? t.next : t.proceed} <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
          {step === 4 && (
            <button
              type="submit" onClick={onSubmit}
              disabled={submitting || cart.length === 0}
              data-testid="cfg-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] disabled:opacity-50 transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? t.submitting : t.submit}
            </button>
          )}
        </div>
      </div>

      <SummaryAside
        t={t}
        step={step}
        cart={cart}
        draft={{ productName, sizeValid, widthNum, heightNum, color, mesh }}
        grandTotal={grandTotal}
        estPrice={estPrice}
        bundleActive={!!bundleInfo}
        disclaimerLabels={disclaimerLabels}
        onRemoveItem={removeItem}
      />
    </div>
  );
}
